/* eslint-disable */
// @ts-nocheck
import * as R from 'ramda';
import css from 'css';
import { userTemplatePatternWithGroups, addUserTemplateDefaultsToTemplate } from './templateUtils';
import { DISMISS_TEXT_ID } from '../journeyConstants';
import type { Language, Tag, CssObject, HtmlParts, CssMap } from '../../../../types/journeyTypes';

/**
 * Takes an DOM element and returns the text value and depth
 * the text was found at
 * @param {object} tag - DOM element
 * @param {languageTable} Language table
 * @return {object} Object containing tag text and depth at which it was found
 */
function getEditableTextForTag(
  tag: Tag,
  languageTable: Language
): {
  tagText: string;
  depth: number;
} {
  var depth = 0;
  languageTable = languageTable || {};

  function findText(tag: Tag): string | null | undefined {
    var text;

    if (tag.type === 'text') {
      return tag.data;
    }

    if (!tag.children) {
      return null;
    }

    for (var i = 0; i < tag.children.length; i++) {
      text = findText(tag.children[i]);

      if (text !== null) {
        depth++;
        return text;
      }
    }

    return null;
  }

  return {
    tagText: (findText(tag) || '').replace(/{{\s?(\$\w+)\s?}}/, function (match, key) {
      return languageTable[key] || '';
    }),
    depth: depth,
  };
}

/**
 * Sets the text for an DOM element
 * @param {object} tag - DOM element
 * @param {string} text - text to be set for tag
 * @return {object} tag - DOM element that was set
 */
function setEditableTextForTag(tag: Tag, text: string): Tag {
  (function setText(tag: Tag): boolean | null | undefined {
    var bWasSet;

    if (tag.type === 'text') {
      tag.data = text;
      return true;
    }

    if (!tag.children) {
      return;
    }

    for (var i = 0; i < tag.children.length; i++) {
      bWasSet = setText(tag.children[i]);

      if (bWasSet) {
        return bWasSet;
      }
    }
  })(tag);

  return tag;
}

/**
 * Gets all styles for a given DOM element
 * @param {object} tag - DOM element
 * @param {object} cssObject - Object representation of css
 * @return {object} Map of styles for given tag
 */
function getStylesForTag(tag: Tag, cssObject: CssObject): CssMap {
  const attribs = tag.attribs;

  if (!attribs || !attribs.class) {
    return {};
  }

  var tagSelector = '#branch-banner .' + attribs.class.split(' ')[0];
  const stylesheet = cssObject.stylesheet;

  if (!stylesheet) {
    return {};
  }

  var styles = R.reduce(
    (style, rule) => {
      if (rule.type !== 'rule') {
        return style;
      }

      if (R.find((selector) => tagSelector === selector, rule.selectors)) {
        R.forEach((decl) => {
          if (decl.type === 'declaration') {
            style[decl.property] = decl.value;
          }
        }, rule.declarations);
      }

      return style;
    },
    {},
    stylesheet.rules
  );
  Object.keys(styles).forEach((i) => {
    // Replaces templated variables in styles with their default values so that style editor does not break
    styles[i] = addUserTemplateDefaultsToTemplate(styles[i]);
  });
  return styles;
}

/**
 * Gets the font-size property for a given DOM element
 * @param {object} tag - DOM element
 * @param {object} cssObject - Object representation of css
 * @return {string} font-size style of the given tag
 */
function getFontSizeForTag(tag: Tag, cssObject: CssObject): string | null | undefined {
  return getStylesForTag(tag, cssObject)['font-size'];
}

/**
 * Sets a style property for a given DOM element
 * @param {object} tag - DOM element
 * @param {object} cssObject - Object representation of css
 * @param {string} property - Css property that will be set
 * @param {string} value - Value to set for property
 */
function setStyleForTag(tag: Tag, cssObject: CssObject | null | undefined, property: string, value: string | number): void {
  const attribs:
    | {
        class?: string;
        id?: string;
      }
    | null
    | undefined = tag.attribs;

  if (!attribs || !cssObject) {
    return;
  }

  var tagSelector =
    attribs.id === 'branch-banner'
      ? '#branch-banner' // $FlowFixMe I suspect tag needs either id or class; need confirmation
      : '#branch-banner .' + attribs.class.split(' ')[0];
  const stylesheet = cssObject.stylesheet;

  if (!stylesheet) {
    return;
  }

  return R.forEach((rule) => {
    if (rule.type === 'rule' && R.find((selector) => tagSelector === selector, rule.selectors)) {
      var declarationFound = false;
      R.forEach((decl) => {
        if (decl.type === 'declaration' && decl.property === property) {
          decl.value = value;
          declarationFound = true;
        }
      }, rule.declarations);

      if (!declarationFound) {
        rule.declarations.push({
          parent: rule,
          property: property,
          type: 'declaration',
          value: value,
        });
      }
    }
  }, stylesheet.rules);
}

/**
 * Removes a style property for a given DOM element
 * @param {object} tag - DOM element
 * @param {object} cssObject - Object representation of css
 * @param {string} property - Css property to remove
 */
function removeStyleForTag(tag: Tag, cssObject: CssObject | null | undefined, property: string): void {
  if (!tag.attribs) {
    return;
  }

  var tagSelector =
    tag.attribs.id === 'branch-banner'
      ? '#branch-banner' // $FlowFixMe I suspect tag needs either id or class; need confirmation
      : '#branch-banner .' + tag.attribs.class.split(' ')[0];

  if (!cssObject) {
    return;
  }

  const stylesheet = cssObject.stylesheet;

  if (!stylesheet) {
    return;
  }

  R.forEach((rule) => {
    if (rule.type === 'rule' && R.find((selector) => tagSelector === selector, rule.selectors)) {
      const propIndex = R.findIndex(R.propEq('property', property), rule.declarations);

      if (propIndex > -1) {
        rule.declarations.splice(propIndex, 1);
      }
    }
  }, stylesheet.rules);
}

/**
 * Returns all items in a list that are editable by the template editor ui
 * @param {array} classlist - List of classes
 * @return {array} List of classes that are editable
 */
function findEditableClasses(classList: Array<string> | string): Array<string> {
  if (typeof classList === 'string') {
    classList = classList.split(' ');
  }

  return R.intersection(classList, [
    'content-box',
    'content',
    'title',
    'branch-banner-continue',
    'description',
    'reviews',
    'stars',
    'icon',
    'branch-animation',
    'button',
    'button-text',
    'branch-banner-close',
    'branch-banner-dismiss-background',
    'branch-banner-content-box',
    'branch-banner-content',
    'branch-banner-title',
    'branch-banner-description',
    'branch-banner-reviews',
    'branch-banner-stars',
    'branch-banner-icon',
    'branch-banner-image',
    'branch-banner-branch-animation',
    'branch-banner-button',
    'branch-banner-button-text',
  ]);
}

/**
 * Returns a boolean, whether any class in list is editable
 * @param {array} classlist - List of classes
 * @return {boolean}
 */
function isSelectableByClasses(classList: Array<string> | string): boolean {
  return !!findEditableClasses(classList).length;
}

/**
 * Searches up the DOM for element with id #branch-banner
 * @param {object} tag - DOM element
 * @return {object} DOM element with id branch-banner
 */
function findBranchBannerTag(tag?: Tag): Tag | void {
  if (!tag) {
    return;
  }

  if (tag.attribs && tag.attribs.id && tag.attribs.id === 'branch-banner') {
    return tag;
  }

  return findBranchBannerTag(tag.parent);
}

/**
 * Returns tag with given id from a list of tags
 * @param {object} tags - list of DOM elements
 * @param {string} tag id
 * @return {object} DOM element with id branch-banner
 */
function findTagByIdInTagList(tags: Record<number, Tag>, tagId: string): Tag | null | undefined {
  const matchingTagKey = R.find((key) => {
    const tag = tags[key];
    return R.path(['attribs', 'id'], tag) === tagId;
  }, Object.keys(tags));
  return tags[matchingTagKey];
}

/**
 * Returns tag with id #branch-banner from a list of tags
 * @param {object} tags - list of DOM elements
 * @return {object} DOM element with id 'branch-banner'
 */
function findBranchBannerInTagList(tags: Record<number, Tag>): Tag | null | undefined {
  return findTagByIdInTagList(tags, 'branch-banner');
}

/**
 * Find a specific property in the cssObject
 * @param {object} cssObject - Object representation of css
 * @param {string} selector - Css query selector
 * @param {string} property - Css property to search for
 * @param {string} returnValue - Which property to return the value of. Defaults to 'value'
 * @return {string} CSS value of given property
 */
function findCssProperty(
  cssObject: CssObject,
  selector: string,
  property: string,
  returnValue: string = 'value'
): string | null | undefined {
  returnValue = returnValue;
  const rules = R.path(['stylesheet', 'rules'], cssObject);

  if (!rules) {
    return;
  }

  const rule = R.find((rule) => {
    return rule.selectors && R.nth(0, rule.selectors) === selector;
  }, rules);

  if (!rule || !rule.declarations) {
    return;
  }

  const declaration = R.find((declaration) => {
    return R.propEq('property', property, declaration);
  }, rule.declarations);
  return declaration ? declaration[returnValue] : null;
}

const DEFAULT_CUSTOM_CONTENT_POSITION = '10';

/**
 * Returns the numerical top margin for a given tag
 * @param {object} tag - DOM element
 * @param {object} cssObject - Object representation of css
 * @return {number} Value of margin-top property for given tag
 */
function getContentPosition(tag: Tag, cssObj: CssObject): string {
  const styles = getStylesForTag(tag, cssObj);
  let contentPosition = R.prop('margin-top', styles);
  contentPosition = contentPosition?.slice(-1) === '%' ? contentPosition?.slice(0, -1) : contentPosition?.slice(0, -2);
  return isNaN(contentPosition) ? DEFAULT_CUSTOM_CONTENT_POSITION : contentPosition;
}

/**
 * Accepts a stylesheet and adds single quotes around templated variables, then returns an AST object via css.parse()
 * @param {string} style - css style sheet
 * @param {object} options - css.parse() options
 * @return {object} AST object - result of calling css.parse() on style
 */
function cssParse(style: string, options?: {} | null | undefined): CssObject {
  return css.parse(
    style.replace(userTemplatePatternWithGroups, function (match, matchGroup1) {
      return "'" + matchGroup1 + "'"; // adds quotes around templated variables so css.parse() doesn't break
    }),
    options
  );
}

/**
 * Accepts an AST object (as cssParse() produces) converts it to a CSS string, then removes single quotes around templated variables
 * @param {object} cssObject - AST object produced by calling cssParse()
 * @return {string} style - css style sheet represented as a string
 */
function cssStringify(cssObject: CssObject): string {
  return css.stringify(cssObject).replace(userTemplatePatternWithGroups, function (match, matchGroup1) {
    return matchGroup1;
  });
}

/**
 * Extract a part of string matching the given regex.
 * Returns empty string if no match is found
 * @param  {string} html
 * @param  {regex} re           regex used for matching
 * @param  {number} matchIndex  index of the matched part to return
 * @return {string}
 */
function extractPart(html, re, matchIndex) {
  var match = html.match(re);

  if (!match) {
    return '';
  }

  return match[matchIndex];
}

/**
 * Extract the style part from HTML template and parse it into CSS
 * string and object for further use
 * @param  {string} originalTemplate
 * @param  {regex} regex              regex to extract the style
 * @return {{ cssObject: Object, cssStyle: string}}
 */
function getCSSObjectAndStyle(
  originalTemplate,
  regex
): {
  cssObject?: CssObject;
  cssStyle?: string;
} {
  const style = extractPart(originalTemplate, regex, 1);
  let cssObject, cssStyle;

  try {
    cssObject = cssParse(style);
    cssStyle = cssStringify(cssObject);
  } catch (e) {}

  return {
    cssObject,
    cssStyle,
  };
}

/**
 * Takes an html string and extracts it into html, css and metadata
 * @param {string} original - original html string
 * @return {htmlParts} Object containing separated html parts
 */
function extractParts(original: string): HtmlParts {
  extractParts.styleRe = /<style type="text\/css" id="branch-css">([\s\S]*?)<\/style>/;
  extractParts.jsonRe = /<script type="application\/json">([\s\S]*?)<\/script>/;
  extractParts.jsRe = /<script type="text\/javascript">([\s\S]*?)<\/script>/;
  extractParts.preBodyRe = /[\s\S]*<body>/;
  extractParts.postBodyRe = /<\/body>[\s\S]*/;
  extractParts.iFrameCSS = /<style type="text\/css" id="branch-iframe-css">((.|\s)*?)<\/style>/;
  // extract stylesheet
  const { cssObject, cssStyle } = getCSSObjectAndStyle(original, extractParts.styleRe);
  // extract json metadata
  const metadataString = extractPart(original, extractParts.jsonRe, 1);
  let metadata;

  try {
    metadata = JSON.parse(metadataString);
  } catch (e) {
    metadata = {};
  }

  // extract iframe style
  const { cssObject: iFrameCSSObject, cssStyle: iFrameCSSStyle } = getCSSObjectAndStyle(original, extractParts.iFrameCSS);
  // clean up html
  var html = original.replace(extractParts.preBodyRe, '');
  html = html.replace(extractParts.postBodyRe, '');
  html = html.replace(extractParts.jsRe, '');
  html = html.replace(extractParts.jsonRe, '');
  html = html.replace(extractParts.styleRe, '');
  html = html.replace(extractParts.iFrameCSS, '');
  return {
    html,
    // style, // the original style, for reference
    original,
    cssObject,
    cssStyle,
    metadata,
    iFrameCSSObject,
    iFrameCSSStyle,
  };
}

/**
 * Converts a height value from vh to px value or vice versa based on viewer height
 * @param {number} value - number or stringified number to convert
 * @param {string} toUnit - Unit to convert to
 * @return {number} Converted number
 */
function convertDimensionValueToOtherUnit(value: number | string, toUnit: string): number {
  value = parseInt(value, 10);
  const conversionRate = 5.32;
  return toUnit === 'px' ? Math.round(value * conversionRate) : Math.round(value / conversionRate);
}

/**
 * Returns the tag with with the given className
 * @param {object} tag - DOM element
 * @param {string} className - className you are searching for
 * @return {object} element - DOM element with given classname
 */
function getElementByClassName(tag: Tag, className: string): Tag | null | undefined {
  const branchBanner = findBranchBannerTag(tag);
  let element;

  function findElement(tag) {
    if (!tag) {
      return;
    }

    const tagClass = R.path(['attribs', 'class'], tag);

    if (tagClass && tagClass.indexOf(className) > -1) {
      element = tag;
    }

    if (!tag.children || !tag.children.length) {
      return;
    }

    R.forEach((child) => {
      findElement(child);
    }, tag.children);
  }

  findElement(branchBanner);
  return element;
}

const getDismissTextFromTags = (tags: Record<number, Tag>, language: Language): string | null | undefined => {
  const dismissTextTag = findTagByIdInTagList(tags, DISMISS_TEXT_ID);

  if (!dismissTextTag) {
    return null;
  }

  return R.path(['tagText'], getEditableTextForTag(dismissTextTag, language));
};

const isClassVisible = (cssObject: CssObject, className: string): boolean => {
  const visibility = findCssProperty(cssObject, `#branch-banner .${className}`, 'visibility');

  // on initial load findCssProperty doesn't work, so if it doesn't find it,
  // default to true
  if (!visibility) {
    return true;
  }

  return visibility === 'visible';
};

const findEditableParentTagId = (child: Tag | null | undefined) => {
  var tag = child?.dataset?.id;

  if (child) {
    if (tag && isSelectableByClasses(child.classList)) {
      var rect = child.getBoundingClientRect();
      return {
        left: rect.left,
        top: rect.top,
        width: rect.width,
        height: rect.height,
        tag: tag,
      };
    }

    return findEditableParentTagId(child.parentElement);
  }

  return {
    left: -10,
    top: -10,
    width: 1,
    height: 1,
    tag: null,
  };
};

function getImagePosition(tag: Tag, cssObject: CssObject) {
  const styles = getStylesForTag(tag, cssObject);
  const imageSize = R.prop('background-size', styles);

  if (imageSize === 'cover') {
    return imageSize;
  } else {
    return R.prop('background-position', styles);
  }
}

function findCurrentStarCount(tag: Tag) {
  const starCount = parseFloat(tag.attribs['data-star-count']);
  return isNaN(starCount) ? 5 : starCount;
}

export {
  getEditableTextForTag,
  setEditableTextForTag,
  getStylesForTag,
  setStyleForTag,
  removeStyleForTag,
  isSelectableByClasses,
  findEditableClasses,
  findBranchBannerTag,
  findTagByIdInTagList,
  findBranchBannerInTagList,
  findCssProperty,
  getContentPosition,
  extractParts,
  convertDimensionValueToOtherUnit,
  getElementByClassName,
  cssParse,
  cssStringify,
  getDismissTextFromTags,
  isClassVisible,
  findEditableParentTagId,
  getFontSizeForTag,
  getImagePosition,
  findCurrentStarCount,
};
